// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { MappedLink } from "@insite/mobius/Menu/Menu";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import FloatingTooltip, { FloatingTooltipStyles } from "@vanmeter/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@vanmeter/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@vanmeter/App/Components/MiniMyAccount/MiniMyAccount";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import HeaderAccount from "@vanmeter/XDL/Icons/IconHeaderAccount";
import { getMyAccountMapLinks } from "@vanmeter/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /**
     * XNG-Changes
     */
    accountManagementLinks = "accountManagementLinks",
    orderManagementLinks = "orderManagementLinks",
    resourcesLinks = "resourcesLinks",
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        /**
         * XNG-Changes
         */
        isAuthenticated: xIsAuthenticated(state),
        isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
        accountManagementLinks: getMyAccountMapLinks(state, ownProps.fields.accountManagementLinks),
        orderManagementLinks: getMyAccountMapLinks(state, ownProps.fields.orderManagementLinks),
        resourcesLinks: getMyAccountMapLinks(state, ownProps.fields.resourcesLinks),
        isTablet: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Changes
    logInButton?: ButtonPresentationProps;
    headerClickable?: HeaderClickableStyles;
    myAccountTooltip?: FloatingTooltipStyles;
    signInDrawerStyles?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // XNG-Changes
        [fields.accountManagementLinks]: LinkModel[];
        [fields.orderManagementLinks]: LinkModel[];
        [fields.resourcesLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    // XNG-Changes
    id,
    isAuthenticated,
    isMyAccountPage,
    accountManagementLinks,
    orderManagementLinks,
    resourcesLinks,
    isTablet,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);
    const closeMyAccountMenu = () => setMenuOpen(false);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    React.useEffect(() => {
        setCookie("showTippy", "true");
    }, []);

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    // XNG-Changes: add isTablet
    const showIcon = (visibilityState === "both" || visibilityState === "icon") && isTablet;
    const showLabel = (visibilityState === "both" || visibilityState === "label") && !isTablet;

    // if (fields.icon === "User") {
    //     icon = User;
    // }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        // XNG-Changes: add isTablet
        if ((!userName || currentUserIsGuest) && !isSignInPage && isTablet) {
            e.preventDefault();

            /**
             * XNG-Changes
             * Stop OOTB Overlay
             */
            // if (theme.signIn?.useOverlay) {
            //     setSignInOverlayIsOpen({ isOpen: true });
            //     return;
            // }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    // XNG-Change: use href so My Account navigates like Cart link
    const myAccountUrl = !isMyAccountPage ? myAccountPageLink?.url : undefined;

    if (!isAuthenticated) {
        return (
            <FloatingTooltip
                onOpenChange={open => setMenuOpen(open)}
                triggerComponent={
                    <Button
                        variant={isTablet ? ("link" as any) : "secondary"}
                        {...styles.logInButton}
                        onClick={onSignInHandler}
                        aria-label={translate("Log In")}
                    >
                        {translate("Log In")}
                    </Button>
                }
                trigger="click"
                enabled={!isTablet && !isSignInPage}
                overlay={true}
                drawer={true}
                offset={0}
                extendedStyles={styles.signInDrawerStyles}
            >
                <XngZone contentId={id} zoneName="conten0" />
            </FloatingTooltip>
        );
    }

    return (
        <StyledWrapper data-test-selector="mini-myaccount-wrapper">
            <FloatingTooltip
                offset={0}
                isOpen={isMenuOpen}
                onOpenChange={open => setMenuOpen(open)}
                showArrow={false}
                triggerComponent={
                    <HeaderClickable
                        isOpen={isMenuOpen}
                        href={myAccountUrl}
                        data-test-selector="myAccount"
                        aria-label={translate("My Account")}
                        showIcon={showIcon}
                        showArrow={!isTablet}
                        icon={{ src: HeaderAccount }}
                        extendedStyles={styles.headerClickable}
                    >
                        {showLabel && translate("My Account")}
                    </HeaderClickable>
                }
                enabled={!isTablet}
                extendedStyles={styles.myAccountTooltip}
                placement="bottom-end"
                overlay={true}
            >
                <MiniMyAccount
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    accountManagementLinks={accountManagementLinks}
                    orderManagementLinks={orderManagementLinks}
                    resourcesLinks={resourcesLinks}
                    onSignOutHandler={onSignOutHandler}
                    onCloseMenu={closeMyAccountMenu}
                />
            </FloatingTooltip>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                // XNG-Changes: add tooltip
                tooltip: "for Authenticated View - 'Both' option Will Depend on Responsive View as Wells",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
                // XNG-Changes
                isVisible: _ => false,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            // XNG-Changes
            {
                displayName: "Account Management",
                name: fields.accountManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state: any) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    return overriddenTitle;
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: (item: HasFields) => false,
                        allowUrls: (item: HasFields) => true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "queryString",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                displayName: "Product & Order Management",
                name: fields.orderManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state: any) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    return overriddenTitle;
                },
                defaultValue: [],
                fieldDefinitions: [
                    // XNG-Changes
                    {
                        name: "isBillTrustLink",
                        displayName: "Is Bill Trust Link",
                        editorTemplate: "CheckboxField" as any,
                        defaultValue: false,
                        tooltip: "a generated link will show for bill trust",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: (item: HasFields) => false,
                        allowUrls: (item: HasFields) => true,
                        // XNG-Changes
                        // no need to enter a link for Bill Trust as we're reading it from session properties
                        isVisible: (item: HasFields) => !item.fields.isBillTrustLink,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "queryString",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        // XNG-Changes
                        isVisible: (item: HasFields) => !item.fields.isBillTrustLink,
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        // XNG-Changes
                        isVisible: (item: HasFields) => !item.fields.isBillTrustLink,
                    },
                ],
            },
            {
                displayName: "Resources",
                name: fields.resourcesLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state: any) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    return overriddenTitle;
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: (item: HasFields) => false,
                        allowUrls: (item: HasFields) => true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "queryString",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
